import { useEffect, useState, useRef } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { EmailUtils } from '@/utils/emailUtils';

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const isInitialized = useRef(false);

  useEffect(() => {
    // Prevent multiple initializations and ensure we only have one auth subscription
    if (isInitialized.current) {
      console.log('⚠️ useAuth already initialized, skipping...');
      return () => {}; // Return empty cleanup function to avoid memory leaks
    }
    
    isInitialized.current = true;
    console.log('🔧 Initializing useAuth hook...');

    const checkSession = async () => {
      try {
        console.log('🔍 Checking initial session...');
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          console.error('❌ Error getting session:', sessionError);
          setLoading(false);
          return;
        }

        if (session?.user) {
          console.log('✅ Session found, setting user');
          setUser(session.user);
          
          try {
            const { data: statusData, error: statusError } = await supabase
              .rpc('check_user_status', { user_id: session.user.id })
              .single();

            if (statusError) {
              console.warn('⚠️ Error checking user status:', statusError);
            } else if ((statusData as any)?.is_active === false) {
              console.log('🚫 Inactive user detected, signing out');
              await supabase.auth.signOut();
              setUser(null);
            }
          } catch (statusCheckError) {
            console.warn('⚠️ User status check failed:', statusCheckError);
          }
        } else {
          console.log('ℹ️ No session found');
          setUser(null);
        }
      } catch (error) {
        console.error('❌ Error in checkSession:', error);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        console.log('🔄 Auth state changed:', _event, !!session);
        
        if (_event === 'SIGNED_OUT') {
          setUser(null);
        } else if (session?.user) {
          setUser(session.user);
        }
      }
    );

    // Simple cleanup on unmount
    return () => {
      console.log('🧹 Cleaning up useAuth hook...');
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      console.log('Attempting to sign in with:', email);
      
      // Clear any existing session first
      await supabase.auth.signOut();
      
      // Attempt login
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) {
        console.error('Sign in error:', error);
        throw error;
      }

      // Verify if user exists and is active
      if (data.user) {
        const { data: statusData, error: statusError } = await supabase
          .rpc('check_user_status', { user_id: data.user.id })
          .single();

        if (statusError) {
          console.warn('⚠️ Error checking user status:', statusError);
          throw new Error('Error verifying user status');
        }
        
        if (statusData && !(statusData as any).is_active) {
          await supabase.auth.signOut();
          console.log('🚫 Login denied for inactive user:', email);
          throw new Error('Account is inactive. Please contact administrator.');
        }
        
        // Set user in state if everything is ok
        setUser(data.user);
        console.log('Sign in successful:', data);
        return data;
      } else {
        throw new Error('No user data returned from authentication');
      }
    } catch (error) {
      console.error('Login process failed:', error);
      // Clear user state on any error
      setUser(null);
      throw error;
    }
  };

  const signInWithAzure = async () => {
    try {
      console.log('Iniciando login com Azure...');
      
      // ✅ Implementação mínima - deixa o Supabase gerenciar tudo
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'azure',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`
        }
      });
      
      if (error) {
        console.error('Azure OAuth error:', error);
        throw error;
      }
      
      console.log('Azure authentication initiated successfully');
    } catch (error) {
      console.error('Azure OAuth error:', error);
      throw error;
    }
  };

  const signUp = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
    });
    if (error) throw error;
    return { user: data.user, session: data.session };
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  const resetPassword = async (email: string) => {
    // Use a URL mais robusta que funciona tanto em dev quanto em produção
    const redirectUrl = `${window.location.origin}/update-password`;
    
    console.log('Reset password redirect URL:', redirectUrl);
    
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl,
    });
    if (error) throw error;
  };

  const uploadAvatar = async (file: File) => {
    if (!user) throw new Error('No user logged in');

    const fileExt = file.name.split('.').pop();
    const fileName = `${user.id}${Math.random().toString(36).substring(2)}.${fileExt}`;
    const filePath = `${fileName}`;

    const { error } = await supabase.storage
      .from('avatars')
      .upload(filePath, file, { upsert: true });

    if (error) throw error;

    const { data } = supabase.storage.from('avatars').getPublicUrl(filePath);
    const avatarUrl = data.publicUrl;

    // Update user's profile with the new avatar URL
    const { error: updateError } = await supabase
      .from('users')
      .update({ avatar_url: avatarUrl })
      .eq('id', user.id);

    if (updateError) throw updateError;

    return avatarUrl;
  };

  return {
    user,
    loading,
    signIn,
    signInWithAzure,
    signUp,
    signOut,
    resetPassword,
    uploadAvatar,
  };
} 